const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_BASE_URL || 'https://api.afriqcm.com';
  }
  return '';
};

const API_BASE_URL = getApiBaseUrl();
const SUPPORT_BASE = API_BASE_URL
  ? `${API_BASE_URL}/v1/api/support`
  : '/v1/api/support';

const handleResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.message || `Request failed (${response.status})`);
    err.statusCode = response.status;
    Object.assign(err, data);
    throw err;
  }
  return data;
};

/**
 * Start a new support conversation.
 * POST /v1/api/support/conversations/start
 * @param {string} email
 * @returns {Promise<{ conversation_id: string, email: string, pin: string }>}
 */
export const startConversation = async (email) => {
  const response = await fetch(`${SUPPORT_BASE}/conversations/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({ email: email.trim() }),
  });
  return handleResponse(response);
};

/**
 * Access an existing conversation with email + PIN.
 * POST /v1/api/support/conversations/access
 * @param {string} email
 * @param {string} pin
 * @returns {Promise<{ conversation: object, pin?: string }>}
 */
export const accessConversation = async (email, pin) => {
  const response = await fetch(`${SUPPORT_BASE}/conversations/access`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({ email: email.trim(), pin: pin.trim() }),
  });
  return handleResponse(response);
};

/**
 * Submit the "Send us a message" contact form. Admin is notified by email.
 * POST /v1/api/support/message
 * @param {Object} payload - first_name, last_name, email, subject, message; optional: phone, inquiry_type
 * @returns {Promise<{ id: string, message: string }>}
 */
export const submitContactMessage = async (payload) => {
  const body = {
    first_name: (payload.first_name || payload.firstName || '').trim().slice(0, 100),
    last_name: (payload.last_name || payload.lastName || '').trim().slice(0, 100),
    email: (payload.email || '').trim(),
    subject: (payload.subject || '').trim().slice(0, 200),
    message: (payload.message || '').trim().slice(0, 5000),
  };
  if ((payload.phone || '').trim()) body.phone = (payload.phone || '').trim().slice(0, 30);
  if ((payload.inquiry_type || payload.inquiryType || '').trim()) {
    body.inquiry_type = (payload.inquiry_type || payload.inquiryType || '').trim().slice(0, 50);
  }
  const response = await fetch(`${SUPPORT_BASE}/message`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Build WebSocket URL for support chat.
 * - Production: uses API base URL; https -> wss, http -> ws (so wss when SSL is installed).
 * - Development: same origin so proxy can forward; same protocol conversion.
 */
export const getSupportWsUrl = (conversationId, email, pin) => {
  if (typeof window === 'undefined') return '';
  const apiBase = process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_API_BASE_URL || 'https://api.afriqcm.com')
    : '';
  const origin = apiBase || window.location.origin;
  const wsProtocol = origin.startsWith('https') ? 'wss' : 'ws';
  const base = origin.replace(/^https?:\/\//i, `${wsProtocol}://`);
  const params = new URLSearchParams({
    conversation_id: conversationId,
    email,
    pin,
  });
  return `${base}/v1/api/support/ws?${params.toString()}`;
};
